import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createGroup,
  ensureInviteCode,
  getUserGroups,
  joinGroup,
  leaveGroup,
  renameGroup,
} from "../services/group-service";
import {
  getUserProfile,
  setActiveGroup as saveActiveGroup,
} from "../services/user-service";

export function useGroups(user) {
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [groupError, setGroupError] = useState("");

  const loadUserGroups = useCallback(async (currentUser, options = {}) => {
    if (!currentUser) {
      setGroups([]);
      return;
    }

    const { restoreActive = false } = options;

    try {
      setGroupsLoading(true);

      let userGroups = await getUserGroups(currentUser.uid);

      // If the user has no groups, create a personal private group automatically
      if ((!userGroups || userGroups.length === 0) && currentUser) {
        try {
          const created = await createGroup("Personal", currentUser);

          // mark it as active for this user
          try {
            await saveActiveGroup(currentUser.uid, created.id);
          } catch (e) {
            console.error("Could not save active group:", e);
          }

          setActiveGroup({
            id: created.id,
            name: created.name,
            inviteCode: created.inviteCode,
          });

          userGroups = [created];
        } catch (e) {
          console.error("Could not create personal group:", e);
        }
      } else if (restoreActive && userGroups.length > 0) {
        // Restore the group the user was last working in.
        let target = null;

        try {
          const profile = await getUserProfile(currentUser.uid);

          if (profile?.activeGroupId) {
            target =
              userGroups.find((group) => group.id === profile.activeGroupId) ||
              null;
          }
        } catch (e) {
          console.error("Could not load user profile:", e);
        }

        // Stored group is missing or no longer accessible: fall back to the first one.
        if (!target) {
          target = userGroups[0];

          try {
            await saveActiveGroup(currentUser.uid, target.id);
          } catch (e) {
            console.error("Could not save active group:", e);
          }
        }

        setActiveGroup({
          id: target.id,
          name: target.name,
          inviteCode: target.inviteCode,
        });
      }

      // Backfill the invite-code lookup for any pre-existing groups this user
      // owns (idempotent; a no-op once the mapping exists).
      await Promise.all(
        (userGroups || []).map((group) =>
          ensureInviteCode(group, currentUser).catch(() => {}),
        ),
      );

      setGroups(userGroups);
    } catch (error) {
      console.error("Could not load groups:", error);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setActiveGroup(null);
      setGroups([]);
      setGroupName("");
      setInviteCode("");
      setGroupError("");
      setGroupsLoading(false);
      return;
    }

    loadUserGroups(user, { restoreActive: true });
  }, [user, loadUserGroups]);

  const handleCreateGroup = useCallback(async () => {
    try {
      setGroupError("");

      if (!groupName.trim() || !user) {
        setGroupError("Enter a group name.");
        return;
      }

      const group = await createGroup(groupName, user);

      await saveActiveGroup(user.uid, group.id);

      setActiveGroup({
        id: group.id,
        name: groupName.trim(),
        inviteCode: group.inviteCode,
      });

      setGroupName("");
    } catch (error) {
      console.error("Create group error:", error);
      setGroupError("Could not create group.");
    }

    if (user) {
      await loadUserGroups(user);
    }
  }, [groupName, user, loadUserGroups]);

  const handleJoinGroup = useCallback(async () => {
    try {
      setGroupError("");

      if (!inviteCode.trim() || !user) {
        setGroupError("Enter an invite code.");
        return;
      }

      const group = await joinGroup(inviteCode, user);

      await saveActiveGroup(user.uid, group.id);

      setActiveGroup({
        id: group.id,
        name: group.name,
        inviteCode: group.inviteCode,
      });

      setInviteCode("");
    } catch (error) {
      if (error.message === "GROUP_NOT_FOUND") {
        setGroupError("Group not found.");
        return;
      }

      if (user) {
        await loadUserGroups(user);
      }

      if (error.message === "GROUP_FULL") {
        setGroupError("This group already has 5 members.");
        return;
      }

      console.error("Join group error:", error);
      setGroupError("Could not join group.");
    }
  }, [inviteCode, user, loadUserGroups]);

  const handleSelectGroup = useCallback(
    async (group) => {
      if (!user) return;

      await saveActiveGroup(user.uid, group.id);

      setActiveGroup({
        id: group.id,
        name: group.name,
        inviteCode: group.inviteCode,
      });
    },
    [user],
  );

  const handleLeaveGroup = useCallback(
    async (group) => {
      if (!user) return;

      try {
        setGroupError("");

        await leaveGroup(group.id, user);

        if (activeGroup?.id === group.id) {
          await saveActiveGroup(user.uid, null);
          setActiveGroup(null);
        }

        await loadUserGroups(user);
      } catch (error) {
        if (error.message === "GROUP_NOT_FOUND") {
          setGroupError("Group not found.");
          return;
        }

        if (error.message === "NOT_A_MEMBER") {
          setGroupError("You are not a member of this group.");
          return;
        }

        if (error.message === "OWNER_CANNOT_LEAVE") {
          setGroupError(
            "Owners can only leave when there is at least one other member.",
          );
          return;
        }

        console.error("Leave group error:", error);
        setGroupError("Could not leave group.");
      }
    },
    [user, loadUserGroups, activeGroup],
  );

  // The new name is supplied by the Groups sheet. The previous window.prompt()
  // was the only browser-native dialog left in the app.
  const handleRenameGroup = useCallback(
    async (group, newName) => {
      if (!group || !user || !newName?.trim()) return;

      try {
        setGroupError("");

        const renamed = await renameGroup(group.id, newName);

        if (activeGroup?.id === renamed.id) {
          setActiveGroup((current) => ({
            ...current,
            name: renamed.name,
          }));
        }

        await loadUserGroups(user);
      } catch (error) {
        if (error.message === "GROUP_NAME_REQUIRED") {
          setGroupError("Enter a group name.");
          return;
        }

        console.error("Rename group error:", error);
        setGroupError("Could not rename group.");
      }
    },
    [user, activeGroup?.id, loadUserGroups],
  );

  const clearActiveGroup = useCallback(() => {
    setActiveGroup(null);
  }, []);

  return useMemo(
    () => ({
      groups,
      groupsLoading,
      activeGroup,
      groupName,
      inviteCode,
      groupError,
      setGroupName,
      setInviteCode,
      handleCreateGroup,
      handleJoinGroup,
      handleLeaveGroup,
      handleSelectGroup,
      handleRenameGroup,
      clearActiveGroup,
    }),
    [
      groups,
      groupsLoading,
      activeGroup,
      groupName,
      inviteCode,
      groupError,
      handleCreateGroup,
      handleJoinGroup,
      handleLeaveGroup,
      handleSelectGroup,
      handleRenameGroup,
      clearActiveGroup,
    ],
  );
}

export default useGroups;
